import React, { useEffect, useMemo, useState } from 'react';
import { RefActiveParticipantsWithAllPlacements, refreshAllDatasets } from '../hooks/DatasetHooks';
import {
  updateSinglePhone,
  updateSingleSensorFiles,
  logBatteryChange,
  updateSingleParticipant,
  markActionItemAsReadyForRevision,
  updateSingleHouse,
  addNoteToActionItem,
  updateSingleEmail,
} from '../store/actions/datasetActions';
import * as libCon from '../community-hats-js-library/Constants.js';
import * as locCon from '../LocalConstants';
import { Button, Card, DatePicker, Input, Space, Spin, Table, Tag, TimePicker } from 'antd';
import { Typography } from 'antd';
import {
  formatDate,
  parseISO,
  subMonths,
  startOfMonth,
  endOfMonth,
  isWithinInterval,
  startOfWeek,
  endOfWeek,
  subWeeks,
} from 'date-fns';
import SimpleBarChart from '../components/charts/barchart/SimpleBarChart';
import {
  formatDistanceShort,
  formatMinutes,
  formatToStudyLocation,
  formatToStudyLocationReadable,
} from '../community-hats-js-library/utils/dateFunctions.js';
import {
  getColorOfExpiry,
  getColorOfLastUpload,
  getTextOfExpiry,
  isAirtableNull,
  isNullOrUndefined,
} from '../community-hats-js-library/utils/generalFunctions.js';
import { SymbolForStatus } from '../community-hats-js-library/utils/devicesStatusFunctions';
import Modal from 'antd/es/modal/Modal';
import dayjs from 'dayjs';
import TextArea from 'antd/es/input/TextArea';
import { getDaysStyle, getEndDateStyle, getFrequencyStyle } from '../community-hats-js-library/utils/styleFunctions';
import { showError, showNotification } from '../utils/generalFunctions.js';
import { RefStoredValue } from '../hooks/GeneralStoreHooks';
import WearableCoverageViewer from '../community-hats-js-library/components/WearableCoverageViewer.js';
import { getUpdatedWearableCoverage } from '../community-hats-js-library/utils/serverFunctions.js';
import { updateObjectValue } from '../store/actions/generalStoreActions.js';
import { EditOutlined, SaveOutlined } from '@ant-design/icons';
import { updateFieldforRecord } from '../community-hats-js-library/back-end/router';
import { CollectionRecordTable, ActionItemTable } from '../components/Tables.js';
import { RefContextLanguage } from '../providers/LanguageProvider';
import { tMessage, tRecordField, tValue } from '../utils/translationFunctions';

const { Title, Text } = Typography;

export const DeviceStatusSection = ({ status, message }) => {
  const i18n = RefContextLanguage();

  return (
    <div style={{ paddingBottom: '18px', display: 'flex', flexDirection: 'column' }}>
      <Text style={{ marginBottom: '2px' }} strong>
        {i18n.t('collectionStatus')}: <SymbolForStatus status={status} />
      </Text>
      <Text>{tMessage(i18n, message)}</Text>
    </div>
  );
};

export const CustomTag = ({ tagText, i }) => {
  return (
    <Tag
      color={libCon.TAG_COLORS[i % libCon.TAG_COLORS.length]}
      style={{
        marginLeft: '3px',
        marginRight: '3px',
      }}
    >
      {tagText}
    </Tag>
  );
};

export const ActionItemSection = ({ actionItem }) => {
  const [isMarkedModalOpen, setIsMarkedModalOpen] = useState(() => false);
  const [isNotesModalOpen, setIsNotesModalOpen] = useState(() => false);
  const [confirmLoading, setConfirmLoading] = useState(() => false);
  const [noteText, setNoteText] = useState(() => null);

  const i18n = RefContextLanguage();

  const localMarkActionItemAsDone = async () => {
    setConfirmLoading(true);

    let status = await markActionItemAsReadyForRevision(actionItem, noteText);

    if (status !== libCon.OK) showError(status);

    setIsMarkedModalOpen(false);
    setConfirmLoading(false);
    setNoteText(null);
  };

  const locaAddNote = async () => {
    setConfirmLoading(true);

    let status = await addNoteToActionItem(actionItem, noteText);

    if (status !== libCon.OK) showError(status);

    setIsNotesModalOpen(false);
    setConfirmLoading(false);
    setNoteText(null);
  };

  return (
    <Card
      key={actionItem[libCon.ID]}
      headStyle={{ backgroundColor: 'var(--primary-color-6-transparent)' }}
      type="inner"
      size="small"
      style={{
        paddingtop: '0px',
        border:
          actionItem[libCon.ATF_SEVERITY] === libCon.ATF_HIGH || actionItem[libCon.ATF_OVERLOOKED] === libCon.YES
            ? '4px solid #b7362d'
            : '',
      }}
      title={actionItem[libCon.ATF_PUBLIC_ID]}
    >
      <div className="verticalSection" style={{ paddingBottom: '15px' }}>
        <Text style={{ marginTop: '2px' }}>
          <strong>{i18n.t('overlooked')}</strong>
        </Text>
        <Text style={{ marginTop: '1px' }}>{tValue(i18n, actionItem[libCon.ATF_OVERLOOKED])}</Text>

        <Text style={{ marginTop: '10px' }}>
          <strong>{i18n.t('priority')}</strong>
        </Text>
        <Text style={{ marginTop: '1px' }}>{tValue(i18n, actionItem[libCon.ATF_SEVERITY])}</Text>

        <p style={{ margin: '1px', marginTop: '20px' }}>
          <strong>{i18n.t('tags')}</strong>
        </p>
        <div className="horizontalSection">
          {actionItem[libCon.ATF_TAGS].map((t, i) => (
            <CustomTag key={i} tagText={t} i={i} />
          ))}
        </div>

        <Text style={{ marginTop: '20px' }}>
          <strong>{i18n.t('description')}</strong>
        </Text>
        <Text style={{ marginTop: '1px' }}>{tRecordField(i18n, actionItem, libCon.ATF_DESCRIPTION)}</Text>

        <Text style={{ marginTop: '20px' }}>
          <strong>{i18n.t('action')}</strong>
        </Text>
        <Text style={{ marginTop: '1px' }}>{tRecordField(i18n, actionItem, libCon.ATF_ACTION)}</Text>

        <Text style={{ marginTop: '20px' }}>
          <strong>{i18n.t('notes')}</strong>
        </Text>
        {isNullOrUndefined(actionItem[libCon.ATF_NOTES]) ? (
          <Text style={{ marginTop: '1px' }}>{i18n.t('noNotes')}</Text>
        ) : (
          actionItem[libCon.ATF_NOTES].split('\n').map((t) => <Text style={{ marginTop: '1px' }}>{t}</Text>)
        )}
        <Button style={{ marginTop: '10px' }} type="primary" onClick={() => setIsNotesModalOpen(true)}>
          {i18n.t('addNote')}
        </Button>

        <div style={{ marginTop: '30px' }}>
          {actionItem[libCon.ATF_STATUS] === libCon.ATF_PENDING ? (
            <Button type="primary" onClick={() => setIsMarkedModalOpen(true)}>
              {i18n.t('sendForRevision')}
            </Button>
          ) : (
            <Text>
              <strong>{i18n.t('actionItemSentForRevision')} </strong>
              <SymbolForStatus status={libCon.DEVICE_STATUS_OK} />
            </Text>
          )}

          <Modal
            title={i18n.t('notes')}
            open={isMarkedModalOpen}
            onCancel={() => setIsMarkedModalOpen(false)}
            onOk={() => localMarkActionItemAsDone()}
            okText={i18n.t('sendForRevision')}
            confirmLoading={confirmLoading}
          >
            <div className="verticalSection">
              <TextArea rows={3} value={noteText} onChange={(e) => setNoteText(e.target.value)} />
            </div>
          </Modal>

          <Modal
            title={i18n.t('notes')}
            open={isNotesModalOpen}
            onCancel={() => setIsNotesModalOpen(false)}
            onOk={() => locaAddNote()}
            okText={i18n.t('addNote')}
            confirmLoading={confirmLoading}
          >
            <div className="verticalSection">
              <TextArea rows={3} value={noteText} onChange={(e) => setNoteText(e.target.value)} />
            </div>
          </Modal>
        </div>
      </div>
    </Card>
  );
};

export const SensorSection = ({ sensor }) => {
  // Updating Sensor
  const [updatingSensor, setUpdatingSensor] = useState(() => false);

  // Battery
  const [batteryDialogOpen, setBatteryDialogOpen] = useState(() => false);
  const [dateBatteryChange, setDateBatteryChange] = useState(() => formatDate(new Date(), 'yyyy-MM-d'));
  const [timeBatteryChange, setTimeBatteryChange] = useState(() => formatDate(new Date(), 'HH:mm'));
  const [isLoggingBatteryChange, setIsLoggingBatteryChange] = useState(false);
  const [batteryChartModalOpen, setBatteryChartModalOpen] = useState(false);

  const i18n = RefContextLanguage();

  const batteryChangeChartData = useMemo(() => {
    const records = sensor[libCon.ATF_BATTERY_CHANGE_RECORDS];
    if (!records || records.length === 0) return [];
    const today = new Date();
    return Array.from({ length: 9 }, (_, i) => {
      const monthDate = subMonths(today, 8 - i);
      const interval = { start: startOfMonth(monthDate), end: endOfMonth(monthDate) };
      const count = records.filter((r) => r['Date'] && isWithinInterval(parseISO(r['Date']), interval)).length;
      return { name: formatDate(monthDate, 'MMM yy'), value: count, label: `${count} change${count !== 1 ? 's' : ''}` };
    });
  }, [sensor]);

  const batteryChangeChartDataWeekly = useMemo(() => {
    const records = sensor[libCon.ATF_BATTERY_CHANGE_RECORDS];
    if (!records || records.length === 0) return [];
    const today = new Date();
    const numWeeks = 39;
    return Array.from({ length: numWeeks }, (_, i) => {
      const weekStart = startOfWeek(subWeeks(today, numWeeks - 1 - i), { weekStartsOn: 1 });
      const weekEnd = endOfWeek(weekStart, { weekStartsOn: 1 });
      const count = records.filter(
        (r) => r['Date'] && isWithinInterval(parseISO(r['Date']), { start: weekStart, end: weekEnd })
      ).length;
      return { name: formatDate(weekStart, 'MMM d'), value: count, label: `${count} change${count !== 1 ? 's' : ''}` };
    });
  }, [sensor]);

  const localLogBatteryChange = async (sensorId, serial) => {
    setBatteryDialogOpen(false);
    setIsLoggingBatteryChange(true);
    let status = await logBatteryChange(sensorId, serial, dateBatteryChange, timeBatteryChange);
    if (status !== libCon.OK) showError(status);

    setIsLoggingBatteryChange(false);
  };

  const refreshSensorFiles = async (sensorSerial) => {
    setUpdatingSensor(true);
    await updateSingleSensorFiles(sensorSerial);
    setUpdatingSensor(false);
  };

  const sensorFilesColumns = [
    {
      title: i18n.t('uploaded'),
      dataIndex: libCon.ATF_DATE_UPLOADED,
      key: libCon.ATF_DATE_UPLOADED,
      align: 'center',
      defaultSortOrder: 'descend',
      sorter: (a, b) => parseISO(a[libCon.ATF_DATE_UPLOADED]) - parseISO(b[libCon.ATF_DATE_UPLOADED]),
      render: (val, record) => (
        <p style={{ textAlign: 'center' }}>
          {formatToStudyLocationReadable(val, false, false)}
          <br /> {formatDistanceShort(val)}
        </p>
      ),
    },
    {
      title: i18n.t('startDate'),
      dataIndex: libCon.ATF_START_DATE,
      key: libCon.ATF_START_DATE,
      align: 'center',
      render: (val, record) => (
        <p style={{ textAlign: 'center' }}>
          {formatToStudyLocationReadable(val, false, false)}
          <br /> {formatDistanceShort(val)}
        </p>
      ),
    },
    {
      title: i18n.t('endDate'),
      dataIndex: libCon.ATF_END_DATE,
      key: libCon.ATF_END_DATE,
      align: 'center',
      render: (val, record) => (
        <p style={{ textAlign: 'center', ...getEndDateStyle(val, record) }}>
          {formatToStudyLocationReadable(val, false, false)}
          <br /> {formatDistanceShort(val)}
        </p>
      ),
    },
    {
      title: i18n.t('freqShort'),
      dataIndex: libCon.ATF_FREQUENCY,
      key: libCon.ATF_FREQUENCY,
      align: 'center',
      render: (val, record) => (
        <p style={{ textAlign: 'center', ...getFrequencyStyle(val, record) }}>{isNullOrUndefined(val) ? '--' : val}</p>
      ),
    },
    {
      title: i18n.t('days'),
      dataIndex: libCon.ATF_SIZE_IN_DAYS,
      key: libCon.ATF_SIZE_IN_DAYS,
      align: 'center',
      render: (val, record) => <p style={{ textAlign: 'center', ...getDaysStyle(val, record) }}>{val}</p>,
    },
  ];

  return (
    <Card
      key={sensor[libCon.ID]}
      headStyle={{ backgroundColor: 'var(--primary-color-5-transparent)' }}
      type="inner"
      size="small"
      style={{ paddingtop: '0px' }}
      title={sensor[libCon.ATF_SERIAL]}
    >
      <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'center', paddingBottom: '15px' }}>
        {updatingSensor === true ? (
          <div>
            <Spin />
          </div>
        ) : (
          <Button type="primary" onClick={() => refreshSensorFiles(sensor[libCon.ATF_SERIAL])}>
            {i18n.t('refresh')}
          </Button>
        )}
      </div>

      <DeviceStatusSection
        status={sensor[libCon.ATF_DEVICE_COLLECTION_STATUS]}
        message={sensor[libCon.ATF_DEVICE_COLLECTION_MESSAGE]}
      />
      <div>
        <p style={{ margin: '1px' }}>
          <strong>{i18n.t('position')}: </strong>
          {tValue(i18n, sensor[libCon.ATF_CURRENT_POSITION])}
        </p>
      </div>
      <div style={{ marginBottom: '15px', display: 'flex', flexDirection: 'row', justifyContent: 'center' }}>
        <Text style={{ marginRight: '10px' }}>
          <Text strong>{i18n.t('brand')}: </Text>
          {sensor[libCon.ATF_BRAND]}
        </Text>
        <Text style={{ marginLeft: '10px' }}>
          <Text strong>{i18n.t('model')}: </Text>
          {sensor[libCon.ATF_MODEL]}
        </Text>
      </div>
      <div>
        <p style={{ margin: '1px' }}>
          <strong>{i18n.t('placementStart')}: </strong>
          {formatToStudyLocation(sensor[libCon.ATF_START_DATE], false)} (
          {formatDistanceShort(sensor[libCon.ATF_START_DATE])})
        </p>
        <p style={{ margin: '1px' }}>
          <strong>{i18n.t('latestBatteryChange')}: </strong>
          {formatToStudyLocation(sensor[libCon.ATF_LATEST_BATTERY_CHANGE], false)} (
          {formatDistanceShort(sensor[libCon.ATF_LATEST_BATTERY_CHANGE])})
        </p>
        <div style={{ marginTop: '10px' }}>
          {isLoggingBatteryChange === true ? (
            <div>
              <Spin />
            </div>
          ) : (
            <Button type="primary" onClick={() => setBatteryDialogOpen(true)}>
              {i18n.t('logBatteryChange')}
            </Button>
          )}
        </div>
        {batteryChangeChartData.length > 0 && (
          <div style={{ marginTop: '10px' }}>
            <Text strong>{i18n.t('batteryChangesLastNineMonths')}</Text>
            <div style={{ cursor: 'pointer' }} onClick={() => setBatteryChartModalOpen(true)}>
              <SimpleBarChart
                data={batteryChangeChartData}
                dimensions={{ width: 280, height: 100, margin: { top: 10, right: 10, bottom: 40, left: 30 } }}
                parameters={{ xLabelFontSize: 9, yLabelFontSize: 9 }}
              />
            </div>
            <Modal
              title={i18n.t('batteryChangesLastNineMonths')}
              open={batteryChartModalOpen}
              onCancel={() => setBatteryChartModalOpen(false)}
              onOk={() => setBatteryChartModalOpen(false)}
              width="90%"
              cancelButtonProps={{ style: { display: 'none' } }}
            >
              <div style={{ display: 'flex', justifyContent: 'center' }}>
                <SimpleBarChart
                  data={batteryChangeChartDataWeekly}
                  dimensions={{ width: 900, height: 300, margin: { top: 20, right: 30, bottom: 60, left: 50 } }}
                  parameters={{ xLabelFontSize: 10, yLabelFontSize: 12 }}
                />
              </div>
            </Modal>
          </div>
        )}

        {/* Creating multiple modals and only calling the last one */}
        <Modal
          title={i18n.t('batteryChange')}
          open={batteryDialogOpen}
          onOk={() => localLogBatteryChange(sensor[libCon.ID], sensor[libCon.ATF_SERIAL])}
          onCancel={() => setBatteryDialogOpen(false)}
          okText={i18n.t('logBatteryChange')}
          cancelText={i18n.t('cancel')}
        >
          <div className="verticalSection">
            <Text>{i18n.t('date')}</Text>
            <DatePicker
              style={{ marginBottom: '10px' }}
              onChange={(d, dateString) => setDateBatteryChange(formatDate(new Date(dateString), 'yyyy-MM-d'))}
              value={dayjs(dateBatteryChange)}
            />
            <Text>{i18n.t('time')}</Text>
            <TimePicker
              style={{ marginBottom: '10px' }}
              onChange={(time) => {
                setTimeBatteryChange(time.format('HH:mm'));
              }}
              value={dayjs(timeBatteryChange, 'HH:mm')}
              format={'HH:mm'}
            />
          </div>
        </Modal>
      </div>
      <div style={{ paddingTop: '15px', marginBottom: '15px', display: 'flex', flexDirection: 'column' }}>
        <Text strong>{i18n.t('uploadedFiles')}</Text>
        {sensor[libCon.ATF_RECEIVED_SENSOR_FILES].length === 0 ? (
          <Text>{i18n.t('noFilesReceivedLastMonth')}</Text>
        ) : (
          <Table
            dataSource={sensor[libCon.ATF_RECEIVED_SENSOR_FILES]}
            columns={sensorFilesColumns}
            size="small"
            pagination={{ pageSize: 5 }}
          />
        )}
      </div>
    </Card>
  );
};

export const PhoneNumberComponent = ({ participant, userType }) => {
  const [phoneNumber, setPhoneNumber] = useState(() =>
    isNullOrUndefined(participant[libCon.ATF_PHONE_NUMBER]) ||
    participant[libCon.ATF_PHONE_NUMBER] === libCon.DEFAULT_PHONE_NUMBER
      ? null
      : participant[libCon.ATF_PHONE_NUMBER]
  );
  const [isEditingPhoneNumber, setIsEditingPhoneNumber] = useState(false);
  const [isUpdatingPhoneNumber, setIsUpdatingPhoneNumber] = useState(false);

  const i18n = RefContextLanguage();

  const updatePhoneNumber = async () => {
    setIsUpdatingPhoneNumber(true);

    // Sets the phone number in the participant object
    let [status, records] = await updateFieldforRecord(
      libCon.AIRTABLE_CONFIG[libCon.AT_TABLE_PARTICIPANTS],
      participant[libCon.ID],
      libCon.ATF_PHONE_NUMBER,
      phoneNumber
    );

    if (status !== libCon.OK) {
      showError(status);
      setIsUpdatingPhoneNumber(false);
      return;
    }

    // Updates the participant in the store
    updateObjectValue(libCon.DATASET_PARTICIPANTS, records);

    showNotification(i18n.t('phoneNumberUpdatedSuccessfully'));

    setIsUpdatingPhoneNumber(false);
    setIsEditingPhoneNumber(false);
  };

  return userType !== locCon.USER_TYPE_SEWA ? (
    <div></div>
  ) : (
    <div style={{ margin: '2vh' }}>
      {isUpdatingPhoneNumber ? (
        <div className="horizontalSection">
          <Spin />
        </div>
      ) : isEditingPhoneNumber ? (
        <div className="horizontalSection">
          <Input
            style={{ width: '200px' }}
            value={phoneNumber}
            onChange={(e) => setPhoneNumber(e.target.value)}
            placeholder={i18n.t('enterPhoneNumber')}
          />
          <SaveOutlined onClick={() => updatePhoneNumber()} style={{ marginLeft: '12px', fontSize: '18px' }} />
        </div>
      ) : (
        <div className="horizontalSection">
          <p style={{ margin: '0px' }}>
            <strong style={{ color: 'black' }}>{i18n.t('phoneNumber')}: </strong>
            {isNullOrUndefined(participant[libCon.ATF_PHONE_NUMBER]) ||
            participant[libCon.ATF_PHONE_NUMBER] === libCon.DEFAULT_PHONE_NUMBER
              ? '---'
              : participant[libCon.ATF_PHONE_NUMBER]}
          </p>
          <EditOutlined
            onClick={() => setIsEditingPhoneNumber(true)}
            style={{ marginLeft: '12px', fontSize: '18px' }}
          />
        </div>
      )}
    </div>
  );
};

export const CollectionRecordSection = ({ participant }) => {
  const [showModal, setShowModal] = useState(false);

  const i18n = RefContextLanguage();

  return (
    <div>
      <Modal
        title={i18n.t('allCollectionRecords')}
        open={showModal}
        onCancel={() => setShowModal(false)}
        onOk={() => setShowModal(false)}
        width="90%"
        cancelButtonProps={{ style: { display: 'none' } }}
      >
        <CollectionRecordTable recordsArray={participant[libCon.ATF_COLLECTION_RECORDS]} />
      </Modal>
      <div style={{ marginTop: '10px', paddingBottom: '10px' }}>
        <Button type="primary" onClick={() => setShowModal(true)}>
          {i18n.t('viewParticipantCollectionRecords')}
        </Button>
      </div>
    </div>
  );
};

export const AllActionItemSection = ({ participant, pendingGroundActionItems, groundTeamActionItems }) => {
  const [showModal, setShowModal] = useState(false);

  const i18n = RefContextLanguage();

  return (
    <div style={{ marginBottom: '3px', display: 'flex', flexDirection: 'column', justifyContent: 'space-around' }}>
      <Modal
        title={i18n.t('allActionItems')}
        open={showModal}
        onCancel={() => setShowModal(false)}
        onOk={() => setShowModal(false)}
        width="90%"
        cancelButtonProps={{ style: { display: 'none' } }}
      >
        <ActionItemTable
          actionItemsArray={groundTeamActionItems}
          colIndex={[
            libCon.ATF_OVERLOOKED,
            libCon.ATF_STATUS,
            libCon.ATF_STATUS_LAST_UPDTAED,
            libCon.ATF_TAGS,
            libCon.ATF_DESCRIPTION,
            libCon.ATF_ACTION,
            libCon.ATF_NOTES,
          ]}
        />
      </Modal>
      <div style={{ marginTop: '10px', paddingBottom: '10px' }}>
        <Button type="primary" onClick={() => setShowModal(true)}>
          {i18n.t('viewAllParticipantActionItems')}
        </Button>
      </div>
      <DeviceStatusSection
        status={participant[libCon.ATF_ISSUES_STATUS]?.[libCon.ATF_DEVICE_COLLECTION_STATUS]}
        message={participant[libCon.ATF_ISSUES_STATUS]?.[libCon.ATF_DEVICE_COLLECTION_MESSAGE]}
      />
      {pendingGroundActionItems.map((iss) => (
        <ActionItemSection key={iss[libCon.ID]} actionItem={iss} />
      ))}
    </div>
  );
};

export const DataCollectionStatusCard = ({ participant }) => {
  const isCorrect =
    participant[libCon.ATF_HAS_ACTIVE_SENSORS] &&
    participant[libCon.ATF_HAS_ACTIVE_WEARABLES] &&
    participant[libCon.ATF_HAS_ACTIVE_PHONES];

  let activeSensors = participant[libCon.ATF_ACTIVE_SENSORS];
  let hasActiveSensors = participant[libCon.ATF_HAS_ACTIVE_SENSORS];

  let phone = participant[libCon.ATF_ACTIVE_PHONE];
  let wearable = participant[libCon.ATF_ACTIVE_WEARABLE];
  let email = participant[libCon.ATF_ACTIVE_EMAIL];
  let pendingGroundActionItems =
    participant[libCon.ATF_PENDING_GROUND_TEAM_ISSUES] === undefined ||
    participant[libCon.ATF_PENDING_GROUND_TEAM_ISSUES] === null
      ? []
      : participant[libCon.ATF_PENDING_GROUND_TEAM_ISSUES];
  let groundTeamActionItems =
    participant[libCon.ATF_GROUND_TEAM_ISSUES] === undefined || participant[libCon.ATF_GROUND_TEAM_ISSUES] === null
      ? []
      : participant[libCon.ATF_GROUND_TEAM_ISSUES];

  const [updatingPhone, setUpdatingPhone] = useState(() => false);
  const [updatingEmail, setUpdatingEmail] = useState(() => false);
  const [updatingParticipant, setUpdatingParticipant] = useState(() => false);
  const [updatingHouse, setUpdatingHouse] = useState(() => false);

  const refreshPhone = async (phoneSerial) => {
    setUpdatingPhone(true);
    await updateSinglePhone(phoneSerial);
    setUpdatingPhone(false);
  };

  const refreshEmail = async () => {
    setUpdatingEmail(true);

    // First Updates Email
    let [status, emailRecord] = await updateSingleEmail(email[libCon.ID]);

    // Checks if token is active
    if (status !== libCon.OK) {
      showError(i18n.t('errorRetrievingFitbitToken'));
      setUpdatingEmail(false);

      return;
    }

    let [response, newRecords] = await getUpdatedWearableCoverage(emailRecord[libCon.ATF_EMAIL]);

    if (response === libCon.OK) {
      updateObjectValue(libCon.DATASET_EMAILS, newRecords);
    } else {
      showError(i18n.t('errorRetrievingWearableCoverage'));
    }

    setUpdatingEmail(false);
  };

  const refreshParticipant = async () => {
    setUpdatingParticipant(true);
    await updateSingleParticipant(participant[libCon.ID]);
    setUpdatingParticipant(false);
  };

  const refreshHouse = async () => {
    setUpdatingHouse(true);
    if (!isNullOrUndefined(participant[libCon.ATF_ACTIVE_HOUSE])) {
      await updateSingleHouse(participant[libCon.ATF_ACTIVE_HOUSE][libCon.ID]);
    }
    setUpdatingHouse(false);
  };

  const userType = RefStoredValue(locCon.USER_TYPE);
  const i18n = RefContextLanguage();

  const [cardTitle, setCardTitle] = useState('');

  useEffect(() => {
    let localTitle = `${participant[libCon.ATF_NAME]} ${participant[libCon.ATF_LAST_NAME]} ${
      participant[libCon.ATF_SEWA_ID]
    }`;

    if (userType !== locCon.USER_TYPE_SEWA) localTitle = participant[libCon.ATF_SEWA_ID];

    setCardTitle(localTitle);
  }, [userType, participant]);

  return (
    <div className="pageContainer">
      <Card
        style={{
          marginTop: '2%',
          border: isCorrect ? '4px solid var(--success-color-1)' : '4px solid var(--warning-color-1)',
        }}
        headStyle={{
          backgroundColor: isCorrect ? 'var(--success-color-1-transparent)' : 'var(--warning-color-1-transparent)',
        }}
        title={cardTitle}
      >
        <div style={{ marginBottom: '15px', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
          <p style={{ margin: '1px', fontSize: '18px' }}>
            <strong>{tValue(i18n, participant[libCon.ATF_AREA])}</strong>
          </p>
          <p style={{ margin: '1px' }}>
            <strong>{i18n.t('dateJoined')}: </strong>
            {formatToStudyLocation(participant[libCon.ATF_JOINED_DATE], false)} (
            {formatDistanceShort(participant[libCon.ATF_JOINED_DATE])})
          </p>
          <p style={{ margin: '1px', color: getColorOfExpiry(participant[libCon.ATF_DAYS_UNTIL_EXPIRE]) }}>
            <strong style={{ color: 'black' }}>{i18n.t('nextCollection')}: </strong>
            {getTextOfExpiry(participant[libCon.ATF_DAYS_UNTIL_EXPIRE])}
          </p>
          {isNullOrUndefined(participant[libCon.ATF_LATEST_COLLECTION_DATE]) ? (
            <p style={{ margin: '1px', color: 'var(--warning-color-1)' }}>
              <strong style={{ color: 'black' }}>{i18n.t('lastCollection')}: </strong>
              {i18n.t('never')}
            </p>
          ) : (
            <p
              style={{
                margin: '1px',
                color: getColorOfLastUpload(participant[libCon.ATF_DAYS_SINCE_LAST_HOUSE_VISIT]),
              }}
            >
              <strong style={{ color: 'black' }}>{i18n.t('lastCollection')}: </strong>
              {`${formatToStudyLocation(participant[libCon.ATF_LATEST_COLLECTION_DATE], true)} (${formatDistanceShort(
                participant[libCon.ATF_LATEST_COLLECTION_DATE]
              )})`}
            </p>
          )}
          <p style={{ margin: '1px' }}>
            <strong style={{ color: 'black' }}>{i18n.t('averageCollectionTime')}: </strong>
            {isNullOrUndefined(participant[libCon.ATF_AVERAGE_COLLECTION_DURATION]) ||
            isAirtableNull(participant[libCon.ATF_AVERAGE_COLLECTION_DURATION])
              ? '---'
              : formatMinutes(participant[libCon.ATF_AVERAGE_COLLECTION_DURATION] / 60)}
          </p>
          <PhoneNumberComponent participant={participant} userType={userType} />
          <CollectionRecordSection participant={participant} />
        </div>

        {/* Action Items */}
        <Card
          headStyle={{ backgroundColor: 'var(--primary-color-6)' }}
          className="no-bottom-margin"
          type="inner"
          size="small"
          style={{ paddingtop: '0px' }}
          title={i18n.t('actionItems')}
        >
          <AllActionItemSection
            participant={participant}
            groundTeamActionItems={groundTeamActionItems}
            pendingGroundActionItems={pendingGroundActionItems}
          />
        </Card>

        {/* Intake Survey */}
        <Card
          headStyle={{ backgroundColor: 'var(--primary-color-7)' }}
          className="no-bottom-margin"
          type="inner"
          size="small"
          style={{ paddingtop: '0px' }}
          title={i18n.t('intakeSurveys')}
        >
          <div
            style={{ marginBottom: '10px', display: 'flex', flexDirection: 'column', justifyContent: 'space-around' }}
          >
            <Text style={{ marginBottom: '5px', fontSize: '16px' }}>
              <strong>{i18n.t('participantIntakeForm')} </strong>
            </Text>
            <Text style={{ marginBottom: '5px' }}>
              <strong>{i18n.t('collectionStatus')}: </strong>{' '}
              <SymbolForStatus
                translation={(v) => tMessage(i18n, v)}
                status={participant[libCon.ATF_PARTICIPANT_INTAKE_FORM]?.[libCon.ATF_DEVICE_COLLECTION_STATUS]}
              />
            </Text>
            <Text style={{ marginBottom: '10px' }}>
              {tMessage(i18n, participant[libCon.ATF_PARTICIPANT_INTAKE_FORM]?.[libCon.ATF_DEVICE_COLLECTION_MESSAGE])}
            </Text>
            <div style={{ marginBottom: '25px' }}>
              {updatingParticipant === true ? (
                <div>
                  <Spin />
                </div>
              ) : (
                <Button type="primary" onClick={() => refreshParticipant()}>
                  {i18n.t('refresh')}
                </Button>
              )}
            </div>
            <Text style={{ marginBottom: '5px', marginTop: '30px', fontSize: '16px' }}>
              <strong>{i18n.t('houseIntakeForm')} </strong>
            </Text>
            <Text style={{ marginBottom: '5px' }}>
              <strong>{i18n.t('collectionStatus')}: </strong>{' '}
              <SymbolForStatus
                translation={(v) => tMessage(i18n, v)}
                status={participant[libCon.ATF_HOUSE_INTAKE_FORM]?.[libCon.ATF_DEVICE_COLLECTION_STATUS]}
              />
            </Text>

            <Text style={{ marginBottom: '10px' }}>
              {tMessage(i18n, participant[libCon.ATF_HOUSE_INTAKE_FORM]?.[libCon.ATF_DEVICE_COLLECTION_MESSAGE])}
            </Text>
            <div style={{ paddingBottom: '30px' }}>
              {updatingHouse === true ? (
                <div>
                  <Spin />
                </div>
              ) : (
                <Button type="primary" onClick={() => refreshHouse()}>
                  {i18n.t('refresh')}
                </Button>
              )}
            </div>
          </div>
        </Card>

        {/* Sensors */}
        <Card
          headStyle={{ backgroundColor: 'var(--primary-color-5)' }}
          className="no-bottom-margin"
          type="inner"
          size="small"
          style={{ paddingtop: '0px' }}
          title={i18n.t('bundleSensors')}
        >
          <div
            style={{ marginBottom: '3px', display: 'flex', flexDirection: 'column', justifyContent: 'space-around' }}
          >
            {hasActiveSensors ? (
              activeSensors.map((s) => <SensorSection key={s[libCon.ATF_SERIAL]} sensor={s} />)
            ) : (
              <div className="verticalSection" style={{ paddingBottom: '250px' }}>
                <SymbolForStatus
                  translation={(v) => tMessage(i18n, v)}
                  status={libCon.DEVICE_STATUS_INCOMPLETE_DEPLOYMENT}
                  message={libCon.MESSAGE_INCOMPLETE_DEPLOYMENT}
                />
                <Text type="danger">{i18n.t('participantHasNoActiveSensors')}</Text>
              </div>
            )}
          </div>
        </Card>

        {/* Phone */}
        <Card
          headStyle={{ backgroundColor: 'var(--primary-color-1)' }}
          className="no-bottom-margin"
          type="inner"
          size="small"
          title={i18n.t('locationTrackerApp')}
        >
          {isNullOrUndefined(phone) ? (
            <Text>{i18n.t('participantHasNoActivePhonePlacement')}</Text>
          ) : (
            <div
              style={{ marginBottom: '15px', display: 'flex', flexDirection: 'column', justifyContent: 'space-around' }}
            >
              <DeviceStatusSection
                status={phone[libCon.ATF_DEVICE_COLLECTION_STATUS]}
                message={phone[libCon.ATF_DEVICE_COLLECTION_MESSAGE]}
              />
              <Text style={{ marginBottom: '15px' }}>
                <Text strong>{i18n.t('phoneSerial')}: </Text>
                {phone[libCon.ATF_SERIAL]}
              </Text>
              <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'center' }}>
                <Text style={{ marginRight: '10px' }}>
                  <Text strong>{i18n.t('lastUploaded')}: </Text>
                  {formatDistanceShort(phone[libCon.ATF_LAST_UPLOADED])}
                </Text>
                <Text style={{ marginLeft: '10px' }}>
                  <Text strong>{i18n.t('latestRecord')}: </Text>
                  {formatDistanceShort(phone[libCon.ATF_LATEST_RECORD])}
                </Text>
              </div>
              <div style={{ marginTop: '5px', display: 'flex', flexDirection: 'row', justifyContent: 'right' }}>
                {updatingPhone === true ? (
                  <div>
                    <Spin />
                  </div>
                ) : (
                  <Button type="primary" onClick={() => refreshPhone(phone[libCon.ATF_SERIAL])}>
                    {i18n.t('refresh')}
                  </Button>
                )}
              </div>
            </div>
          )}
        </Card>

        {/* Wearable */}
        <Card
          headStyle={{ backgroundColor: 'var(--primary-color-3)' }}
          className="no-bottom-margin"
          type="inner"
          size="small"
          title={i18n.t('fitbitDevice')}
        >
          {isNullOrUndefined(wearable) || isNullOrUndefined(email) ? (
            <Text>{i18n.t('participantHasNoActiveFitbitPlacement')}</Text>
          ) : (
            <div
              style={{ marginBottom: '15px', display: 'flex', flexDirection: 'column', justifyContent: 'space-around' }}
            >
              <DeviceStatusSection
                status={email[libCon.ATF_DEVICE_COLLECTION_STATUS]}
                message={email[libCon.ATF_DEVICE_COLLECTION_MESSAGE]}
              />
              <Text style={{ marginBottom: '15px' }}>
                <Text strong>{i18n.t('fitbitSerial')}: </Text>
                {wearable[libCon.ATF_SERIAL]}
              </Text>
              <Text style={{ marginBottom: '15px' }}>
                <Text strong>{i18n.t('email')}: </Text>
                {email[libCon.ATF_EMAIL]}
              </Text>

              <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'center' }}>
                <Text
                  style={{
                    marginRight: '10px',
                    color: email[libCon.ATF_GOOGLE_TOKEN_STATUS] !== libCon.ATF_ACTIVE ? 'red' : 'black',
                  }}
                >
                  <Text
                    style={{ color: email[libCon.ATF_GOOGLE_TOKEN_STATUS] !== libCon.ATF_ACTIVE ? 'red' : 'black' }}
                    strong
                  >
                    {i18n.t('apiTokenStatus')}:{' '}
                  </Text>
                  {isNullOrUndefined(email[libCon.ATF_GOOGLE_TOKEN_STATUS])
                    ? '---'
                    : tValue(i18n, email[libCon.ATF_GOOGLE_TOKEN_STATUS])}
                </Text>
              </div>

              <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'center' }}>
                <Text style={{ marginRight: '10px' }}>
                  <Text strong>{i18n.t('lastSynced')}: </Text>
                  {isNullOrUndefined(email[libCon.ATF_LAST_UPLOADED])
                    ? i18n.t('never')
                    : `${formatToStudyLocationReadable(
                        email[libCon.ATF_LAST_UPLOADED],
                        true,
                        false
                      )} (${formatDistanceShort(email[libCon.ATF_LAST_UPLOADED])})`}
                </Text>
              </div>
              <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'center' }}>
                <Text style={{ marginRight: '10px' }}>
                  <Text strong>{i18n.t('lastHeartRateUpload')}: </Text>
                  {isNullOrUndefined(email[libCon.ATF_LATEST_HEART_RATE_UPLOAD])
                    ? i18n.t('never')
                    : `${formatToStudyLocationReadable(
                        email[libCon.ATF_LATEST_HEART_RATE_UPLOAD],
                        true,
                        false
                      )} (${formatDistanceShort(email[libCon.ATF_LATEST_HEART_RATE_UPLOAD])})`}
                </Text>
              </div>
              <div style={{ marginTop: '1px', paddingBottom: '25px' }}>
                <WearableCoverageViewer participant={participant} email={email} updatingEmail={updatingEmail} />
              </div>
              <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'right' }}>
                {updatingEmail === true ? (
                  <div>
                    <Spin />
                  </div>
                ) : (
                  <Button type="primary" onClick={() => refreshEmail()}>
                    {i18n.t('refresh')}
                  </Button>
                )}
              </div>
            </div>
          )}
        </Card>

        {/* Intake Survey */}
        <Card
          headStyle={{ backgroundColor: 'var(--primary-color-7)' }}
          className="no-bottom-margin"
          type="inner"
          size="small"
          style={{ paddingtop: '0px' }}
          title={i18n.t('perceptualSurvey')}
        >
          <div
            style={{ marginBottom: '3px', display: 'flex', flexDirection: 'column', justifyContent: 'space-around' }}
          >
            <Text style={{ marginBottom: '25px' }}>
              <strong>
                {tMessage(i18n, participant[libCon.ATF_PERCEPTUAL_SURVEY]?.[libCon.ATF_DEVICE_COLLECTION_MESSAGE])}{' '}
              </strong>
              <SymbolForStatus
                translation={(v) => tMessage(i18n, v)}
                status={participant[libCon.ATF_PERCEPTUAL_SURVEY]?.[libCon.ATF_DEVICE_COLLECTION_STATUS]}
              />
            </Text>
          </div>

          <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'center' }}>
            <Text style={{ marginRight: '10px' }}>
              <Text strong>{i18n.t('lastUploaded')}: </Text>
              {formatToStudyLocationReadable(participant[libCon.ATF_LAST_UPLOADED_PERCEPTUAL_SURVEY], true)} (
              {formatDistanceShort(participant[libCon.ATF_LAST_UPLOADED_PERCEPTUAL_SURVEY])})
            </Text>
          </div>

          <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'right' }}>
            {updatingParticipant === true ? (
              <div>
                <Spin />
              </div>
            ) : (
              <Button type="primary" onClick={() => refreshParticipant()}>
                {i18n.t('refresh')}
              </Button>
            )}
          </div>
        </Card>
      </Card>
    </div>
  );
};

function DashboardCollectionStatus() {
  // Participants
  const [status, participants] = RefActiveParticipantsWithAllPlacements();

  const [participantArray, setParticipantArray] = useState([]);

  const [inputValue, setInputValue] = useState(() => null);
  const [filterText, setFilterText] = useState(() => null);

  const i18n = RefContextLanguage();

  useEffect(() => {
    if (participants !== null) {
      let parArray = Object.values(participants);
      if (filterText !== null && filterText !== undefined && filterText !== '')
        parArray = parArray.filter((ob) => ob[libCon.ATF_PUBLIC_ID].toUpperCase().includes(filterText.toUpperCase()));

      parArray.sort((a, b) => new Date(b[libCon.ATF_JOINED_DATE]) - new Date(a[libCon.ATF_JOINED_DATE]));
      if (parArray.length > 0) parArray = parArray.slice(0, Math.min(10, parArray.length));

      setParticipantArray(parArray);
    }
  }, [participants, filterText]);

  const handleInputChange = (e) => {
    setInputValue(e.target.value);
  };

  return (
    <div className="mainContainer">
      <Title level={2}>{i18n.t('participantsDataCollectionStatus')}</Title>
      <Space direction="vertical">
        <Space direction="horizontal">
          <Button type="primary" onClick={() => refreshAllDatasets()}>
            {i18n.t('refreshAll')}
          </Button>
          <Input placeholder={i18n.t('filter')} value={inputValue} onChange={handleInputChange} />
          <Button type="primary" onClick={() => setFilterText(inputValue)}>
            {i18n.t('filter')}
          </Button>
          <Button
            type="primary"
            onClick={() => {
              setInputValue(null);
              setFilterText(null);
            }}
            danger
          >
            {i18n.t('clear')}
          </Button>
        </Space>
      </Space>

      <div style={{ marginTop: '5%' }}>
        {status === libCon.STATUS_NOT_STARTED || status === libCon.LOADING || participants === null ? (
          <Spin />
        ) : status === libCon.ERROR ? (
          <div>{i18n.t('error')}</div>
        ) : (
          <div>
            {participantArray.length > 0 ? (
              participantArray.map((par) => <DataCollectionStatusCard key={par[libCon.ID]} participant={par} />)
            ) : (
              <Title level={3}>{i18n.t('noRecordsFound')}</Title>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default DashboardCollectionStatus;
